import { Container, Stack, Text, Title } from "@mantine/core";
import { useMemo } from "react";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import {
  DictionaryManagement,
  DictionaryService,
} from "../../components/feature-specific/DictionaryManagement";
import {
  CertificateCreateRequest,
  CertificateDictionaryModel,
  CertificateUpdateRequest,
  ConsolidateCertificatesRequest,
} from "../../models/certificate.model";
import { CertificateService } from "../../services/certificateService";

export function OperationsCertificatesManagement() {
  const certificateService = useMemo(() => new CertificateService(), []);

  // Adapt CertificateService to DictionaryService interface
  const dictionaryService: DictionaryService<CertificateDictionaryModel> =
    useMemo(
      () => ({
        getDictionary: () => certificateService.getCertificatesDictionary(),
        createItem: (item: Omit<CertificateDictionaryModel, "id">) =>
          certificateService.createCertificate({
            title: item.title,
          } as CertificateCreateRequest),
        updateItem: (id: number, item: Partial<CertificateDictionaryModel>) =>
          certificateService.updateCertificate(id, {
            id,
            title: item.title!,
          } as CertificateUpdateRequest),
        deleteItem: (id: number) => certificateService.deleteCertificate(id),
        consolidateItems: (sourceId: number, targetId: number) =>
          certificateService.consolidateCertificates({
            sourceCertificateId: sourceId,
            targetCertificateId: targetId,
          } as ConsolidateCertificatesRequest),
      }),
      [certificateService]
    );

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Breadcrumb
            items={[
              { label: "Operations Panel", href: "/operations" },
              { label: "Dictionaries", href: "/operations/dictionaries" },
              { label: "Certificates Management", isActive: true },
            ]}
          />
          <Title order={1} size="h2" fw={700}>
            Certificates Management
          </Title>
          <Text c="dimmed" size="lg">
            Manage professional certifications and qualifications
          </Text>
        </div>

        <DictionaryManagement
          title="Certificates"
          service={dictionaryService}
          itemDisplayName="Certificate"
          itemDisplayNamePlural="Certificates"
          canConsolidate={true}
          titleFieldName="title"
          searchPlaceholder="Search certificates..."
        />
      </Stack>
    </Container>
  );
}
